import React from "react";
import {
    Popper,
    Autocomplete,
    Box,
    Button,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    DeleteOutline,
    DragIndicator,
    InfoOutlined,
} from "@mui/icons-material";
import { Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "../../../molecules/StrictModeDroppable";
import {
    type Question,
    WorkflowTriggerAction,
} from "../../../../types/rubric";
import type { AnswerValue } from "../../../../types/answerSet";
import {
    SPAN_ANSWERED_ID,
    SPAN_NOT_ANSWERED_ID,
    supportsCrossWorkflowSources,
    supportsCrossWorkflowTargets,
} from "./workflowTriggerCapabilities";
import {
    getActiveAnswers,
    getTriggerPhase,
    isShowWhenTrigger,
    isSpanQuestionType,
    questionCardPalette,
    type CrossWorkflowSourceExtension,
    type TriggerSectionRenderProps,
    type WorkflowQuestionWithClientId,
} from "./triggerSectionShared";

type ConditionQuestionOption = {
    questionId: number;
    label: string;
    disabled?: boolean;
    /** Group label for MUI Autocomplete groupBy in the cross-workflow source picker. */
    group?: string;
}

/**
 * Cross-workflow source group labels for the SPAN-host from-question picker.
 * Declaration order drives the listbox order via iteration in
 * buildConditionQuestionOptions.
 */
const SOURCE_GROUP_IN_WORKFLOW = "In this workflow";
const SOURCE_GROUP_CODE_WORKFLOWS = "From code workflows";
const SOURCE_GROUP_CASE_WORKFLOWS = "From case workflows";

const operatorOptions = [{ value: "IS", label: "is" }];
const actionOptions = [
    { value: "GO_TO", label: "Go to question" },
    { value: "STOP", label: "Stop workflow" },
    { value: "SET_ANSWERS", label: "Set answers" },
    { value: "AUTOFILL", label: "Autofill answer" },
];

// SPAN/SPANS questions only support STOP and GO_TO actions (no answer set to filter/autofill)
const spanActionOptions = [
    { value: "GO_TO", label: "Go to question" },
    { value: "STOP", label: "Stop workflow" },
];

const spanConditionOptions = [
    { value: SPAN_ANSWERED_ID, label: "Span is selected" },
    { value: SPAN_NOT_ANSWERED_ID, label: "No span selected" },
];

const actionConfig: Record<string, {
    hasInput: boolean;
    inputType?: string;
    inputLabel?: string;
    placeholder?: string;
    needsToAnswerValueIds?: boolean;
}> = {
    GO_TO: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question'
    },
    STOP: {
        hasInput: false
    },
    SET_ANSWERS: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question',
        needsToAnswerValueIds: true
    },
    AUTOFILL: {
        hasInput: true,
        inputType: 'question-select',
        inputLabel: 'Target Question',
        placeholder: 'Select target question',
        needsToAnswerValueIds: true
    }
};

const formatConditionQuestionLabel = (
    questionName: string,
    questionType?: string,
    suffix?: string,
) => `${questionName}${questionType === "SYSTEM" ? " (System)" : ""}${suffix ? ` (${suffix})` : ""}`;

const buildConditionQuestionOptions = (
    allQuestions: WorkflowQuestionWithClientId[],
    hostQuestionId: number,
    selectedQuestionId?: number,
    globalQuestionPool: Question[] = [],
    crossWorkflowSourceExtension?: CrossWorkflowSourceExtension,
): ConditionQuestionOption[] => {
    const hostIndex = allQuestions.findIndex((question) => question.question.id === hostQuestionId);
    const baseOptions = hostIndex === -1 ? allQuestions : allQuestions.slice(0, hostIndex + 1);

    // Group field drives MUI Autocomplete groupBy.
    // Within-workflow options also receive a group when the extension is
    // active so the listbox renders one consistent grouped layout. SPAN/
    // SPANS questions inside cross-workflow sources are filtered out so
    // authors choose real answered workflow questions instead of parent
    // span-selection state.
    const hasExtension = !!crossWorkflowSourceExtension;
    const inWorkflowGroup = hasExtension ? SOURCE_GROUP_IN_WORKFLOW : undefined;
    const codeWorkflowOptions: ConditionQuestionOption[] = (
        crossWorkflowSourceExtension?.codeWorkflows ?? []
    )
        .filter((wq) => wq.question.id !== hostQuestionId)
        .filter((wq) => !isSpanQuestionType(wq.question.questionType))
        .map((wq) => ({
            questionId: wq.question.id,
            label: formatConditionQuestionLabel(wq.question.name, wq.question.questionType),
            group: SOURCE_GROUP_CODE_WORKFLOWS,
        }));
    const caseWorkflowOptions: ConditionQuestionOption[] = (
        crossWorkflowSourceExtension?.caseWorkflows ?? []
    )
        .filter((wq) => wq.question.id !== hostQuestionId)
        .filter((wq) => !isSpanQuestionType(wq.question.questionType))
        .map((wq) => ({
            questionId: wq.question.id,
            label: formatConditionQuestionLabel(wq.question.name, wq.question.questionType),
            group: SOURCE_GROUP_CASE_WORKFLOWS,
        }));
    const appendExtension = (base: ConditionQuestionOption[]): ConditionQuestionOption[] => {
        if (!hasExtension) {
            return base;
        }
        return [...base, ...codeWorkflowOptions, ...caseWorkflowOptions];
    };

    if (selectedQuestionId == null || baseOptions.some((question) => question.question.id === selectedQuestionId)) {
        return appendExtension(
            baseOptions.map((workflowQuestion) => ({
                questionId: workflowQuestion.question.id,
                label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
                group: inWorkflowGroup,
            })),
        );
    }

    // If the selected id resolves into the cross-workflow extension, render it
    // under its group label rather than the "removed from workflow" fallback.
    const matchedExternal =
        codeWorkflowOptions.find((opt) => opt.questionId === selectedQuestionId) ??
        caseWorkflowOptions.find((opt) => opt.questionId === selectedQuestionId);
    if (matchedExternal) {
        return appendExtension(
            baseOptions.map((workflowQuestion) => ({
                questionId: workflowQuestion.question.id,
                label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
                group: inWorkflowGroup,
            })),
        );
    }

    const selectedQuestion = allQuestions.find((question) => question.question.id === selectedQuestionId);
    if (!selectedQuestion) {
        // Fall back to the global question pool so a question that's been
        // removed from the workflow still shows its NAME (with a "removed
        // from workflow" suffix) instead of leaking the numeric id. Only
        // shows the bare id if the question is gone from the system entirely.
        const removedQuestion = globalQuestionPool.find((q) => q.id === selectedQuestionId);
        return appendExtension([
            ...baseOptions.map((workflowQuestion) => ({
                questionId: workflowQuestion.question.id,
                label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
                group: inWorkflowGroup,
            })),
            {
                questionId: selectedQuestionId,
                label: removedQuestion
                    ? formatConditionQuestionLabel(removedQuestion.name, removedQuestion.questionType, "removed from workflow")
                    : `Question #${selectedQuestionId} (removed from workflow)`,
                disabled: true,
                group: inWorkflowGroup,
            },
        ]);
    }

    return appendExtension([
        ...baseOptions.map((workflowQuestion) => ({
            questionId: workflowQuestion.question.id,
            label: formatConditionQuestionLabel(workflowQuestion.question.name, workflowQuestion.question.questionType),
            group: inWorkflowGroup,
        })),
        {
            questionId: selectedQuestion.question.id,
            label: formatConditionQuestionLabel(selectedQuestion.question.name, selectedQuestion.question.questionType, "later question"),
            disabled: true,
            group: inWorkflowGroup,
        },
    ]);
}

const TriggerSectionImpl: React.FC<TriggerSectionRenderProps> = ({
    workflowQuestion,
    section,
    questions,
    validTargetQuestions,
    allQuestions = [],
    globalQuestionPool = [],
    crossWorkflowTargetQuestions = [],
    crossWorkflowSourceQuestions,
    hostCodeType,
    onRemoveTrigger,
    onTriggerAnswersChange,
    onTriggerTargetChange,
    onTriggerActionChange,
    onTriggerToAnswersChange,
    onConditionClauseChange,
    onAddConditionClause,
    onRemoveConditionClause,
    onOperatorChange,
    getAnswersForQuestion,
}) => {
    const question = workflowQuestion.question;
    const clientId = workflowQuestion.clientId;
    const triggerEntries = (workflowQuestion.triggers ?? [])
        .map((trigger, triggerIndex) => ({ trigger, triggerIndex }))
        .filter(({ trigger }) =>
            section.phase === "PRE"
                ? isShowWhenTrigger(trigger)
                : getTriggerPhase(trigger) === "POST" && !isShowWhenTrigger(trigger)
        );

    // Triggers are not available for FREE_TEXT questions since rules cannot be evaluated on free text responses
    if (section.phase === "POST" && question.questionType === "FREE_TEXT") {
        return (
            <Stack spacing={1.5} sx={{ width: "100%", flexDirection: "column", backgroundColor: questionCardPalette.background }}>
                <Typography variant="subtitle2">Triggers</Typography>
                <Typography variant="body2" color="text.secondary">
                    Triggers are not available for free text questions. Rules cannot be evaluated on free text responses.
                </Typography>
            </Stack>
        );
    }

    const isSpan = isSpanQuestionType(question.questionType);

    // SYSTEM questions use synthetic true/false answers based on expression result
    // SPAN/SPANS questions use sentinel IDs for condition rules (handled separately in the UI)
    const activeAnswers: AnswerValue[] = question.questionType === 'SYSTEM'
        ? [
            { id: -1, name: 'true', displayText: 'Expression is true', active: true } as AnswerValue,
            { id: -2, name: 'false', displayText: 'Expression is false', active: true } as AnswerValue,
        ]
        : isSpan
        ? [
            { id: SPAN_ANSWERED_ID, name: 'span_answered', displayText: 'Span is selected', active: true } as AnswerValue,
            { id: SPAN_NOT_ANSWERED_ID, name: 'span_not_answered', displayText: 'No span selected', active: true } as AnswerValue,
        ]
        : getActiveAnswers(question);
    // Host-workflow target options (used for STOP / GO_TO / SET_ANSWERS
    // triggers and as the base set for AUTOFILL).
    // SystemQuestions can target both SystemQuestions and WorkflowQuestions;
    // WorkflowQuestions can only target other WorkflowQuestions (the parent
    // controls scope by the `validTargetQuestions` it passes in).
    // SPAN-typed host workflows treat GO_TO as a visibility gate rather than
    // navigation; GO_TO <self> is allowed as the positive-case render gate
    // paired with a STOP for the negative case. Other host code types keep
    // the original "can't navigate to self" rule.
    const hostTargetOptions: { id: number; name: string }[] = (validTargetQuestions ?? [])
        .filter((q) => hostCodeType === "SPAN" || q.question.id !== question.id)
        .map((q) => ({
            id: q.question.id,
            name: q.question.name,
        }));            

    // Cross-workflow target options (PROCEDURE/DIAGNOSIS/MODIFIER questions
    // from sibling workflows in the rubric) only surface for actions that
    // support cross-workflow targets when the parent passed them in
    // (case-host only). Currently AUTOFILL is the only supported action.
    // SPAN workflow exclusion and host-CASE gating happen in the parent;
    // additionally filter SPAN/SPANS *questions* here, since the backend
    // rejects SPAN-typed questions as cross-workflow targets
    // (no answer-set to fill).
    const crossWorkflowTargetOptions: { id: number; name: string }[] = (
        crossWorkflowTargetQuestions ?? []
    )
        .filter((q) => q.question.id !== question.id)
        .filter((q) => !isSpanQuestionType(q.question.questionType))
        .map((q) => ({
            id: q.question.id,
            name: q.question.name,
        }));

    const computeTargetOptionsForAction = (
        action: WorkflowTriggerAction | undefined,
    ): { id: number; name: string }[] => {
        if (supportsCrossWorkflowTargets(action)) {
            // Concatenate host then cross-workflow so within-workflow
            // targets remain available alongside cross-workflow ones.
            const seen = new Set<number>();
            const merged: { id: number; name: string }[] = [];
            for (const opt of [...hostTargetOptions, ...crossWorkflowTargetOptions]) {
                if (!seen.has(opt.id)) {
                    seen.add(opt.id);
                    merged.push(opt);
                }
            }
            return merged;
        }
        return hostTargetOptions;
    };


    return (
        <Stack spacing={1.5} sx={{ width: "100%", flexDirection: "column", backgroundColor: questionCardPalette.background }}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ md: "center" }}
            >
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="subtitle2">{section.title}</Typography>
                    <Tooltip title="Triggers execute top to bottom. When multiple triggers match the same answer, all execute. For conflicting actions, the last trigger takes priority.">
                        <InfoOutlined fontSize="small" sx={{ color: "text.secondary", fontSize: 16 }} />
                    </Tooltip>
                </Stack>
                <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => section.onAdd?.(clientId)}
                    disabled={section.phase === "POST" && activeAnswers.length === 0}
                >
                    {section.addLabel}
                </Button>
            </Stack>

            {triggerEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {section.emptyText}
                </Typography>
            ) : (
                <StrictModeDroppable droppableId={`${section.droppablePrefix}-${clientId}`} type="TRIGGER">
                    {(triggerDroppableProvided) => (
                        <Stack spacing={1.5} ref={triggerDroppableProvided.innerRef} {...triggerDroppableProvided.droppableProps}>
                            {triggerEntries.map(({ trigger, triggerIndex }, displayIndex) => (
                                <Draggable
                                    key={`trigger-${clientId}-${trigger._clientId ?? trigger.id ?? triggerIndex}`}
                                    draggableId={`trigger-${clientId}-${trigger._clientId ?? trigger.id ?? triggerIndex}`}
                                    index={displayIndex}
                                >
                                    {(triggerDraggableProvided) => (
                                        <Box
                                            ref={triggerDraggableProvided.innerRef}
                                            {...triggerDraggableProvided.draggableProps}
                                            sx={{
                                                border: `1px solid ${questionCardPalette.border}`,
                                                backgroundColor: questionCardPalette.triggerBackground,
                                                borderRadius: "3px",
                                                p: 2,
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Box
                                                        component="span"
                                                        {...triggerDraggableProvided.dragHandleProps}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            cursor: "grab",
                                                            color: "text.secondary",
                                                        }}
                                                    >
                                                        <DragIndicator fontSize="small" />
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        #{displayIndex + 1}
                                                    </Typography>
                                                </Stack>
                                                {/* Compound condition clauses */}
                                                {(trigger.compoundCondition?.conditions ?? [{ questionId: question.id, answerValueIds: trigger.answerValueIds ?? [] }]).map((clause, clauseIndex) => {
                                                    const clauseAnswers = getAnswersForQuestion(clause.questionId);

                                                    const sourceExtensionEnabled =
                                                        supportsCrossWorkflowSources(
                                                            trigger.action,
                                                            getTriggerPhase(trigger),
                                                            hostCodeType,
                                                        );
                                                    const clauseQuestionOptions = buildConditionQuestionOptions(
                                                        allQuestions,
                                                        question.id,
                                                        clause.questionId,
                                                        globalQuestionPool,
                                                        sourceExtensionEnabled
                                                            ? {
                                                                  codeWorkflows: crossWorkflowSourceQuestions?.codeWorkflows ?? [],
                                                                  caseWorkflows: crossWorkflowSourceQuestions?.caseWorkflows ?? [],
                                                              }
                                                            : undefined,
                                                    );
                                                    const showNonCoAssignedWarning = (() => {
                                                        if (!sourceExtensionEnabled) return false;
                                                        const fromId = clause.questionId;
                                                        if (fromId == null) return false;
                                                        // Host-workflow questions are always reachable.
                                                        if (allQuestions.some((wq) => wq.question.id === fromId)) return false;
                                                        // If the id resolves into the loaded cross-workflow source
                                                        // pool, the workflow is at least active in the system —
                                                        // treat it as co-assigned for v1 purposes.
                                                        const inCrossPool =
                                                            (crossWorkflowSourceQuestions?.codeWorkflows ?? []).some(
                                                                (wq) => wq.question.id === fromId,
                                                            ) ||
                                                            (crossWorkflowSourceQuestions?.caseWorkflows ?? []).some(
                                                                (wq) => wq.question.id === fromId,
                                                            );
                                                        return !inCrossPool;
                                                    })();

                                                    return (
                                                        <React.Fragment key={`clause-${clauseIndex}`}>
                                                            {clauseIndex > 0 && (
                                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                                                        <Select
                                                                            value={trigger.compoundCondition?.operator ?? "AND"}
                                                                            onChange={(event) => {
                                                                                if (onOperatorChange) {
                                                                                    onOperatorChange(clientId, triggerIndex, event.target.value as "AND" | "OR");
                                                                                }
                                                                            }}
                                                                        >
                                                                            <MenuItem value="AND">AND</MenuItem>
                                                                            <MenuItem value="OR">OR</MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                </Stack>
                                                            )}
                                                            <Stack
                                                                direction={{ xs: "column", md: "row" }}
                                                                spacing={1}
                                                                alignItems={{ md: "center" }}
                                                            >
                                                                <Typography variant="subtitle2">If</Typography>

                                                                <Autocomplete<ConditionQuestionOption, false, false, false>
                                                                    sx={{ flex: 1, minWidth: 0 }}
                                                                    size="small"
                                                                    fullWidth
                                                                    PopperComponent={(popperProps) => {
                                                                        const anchorWidth =
                                                                            popperProps.anchorEl instanceof HTMLElement
                                                                                ? popperProps.anchorEl.clientWidth
                                                                                : undefined;
                                                                        return (
                                                                            <Popper
                                                                                {...popperProps}
                                                                                style={{
                                                                                    ...popperProps.style,
                                                                                    width: anchorWidth ? `${anchorWidth}px` : popperProps.style?.width,
                                                                                }}
                                                                            />
                                                                        );
                                                                    }}
                                                                    slotProps={{
                                                                        paper: {
                                                                            sx: {
                                                                                width: "100%",
                                                                                "& .MuiAutocomplete-listbox": {
                                                                                    width: "100%",
                                                                                    boxSizing: "border-box",
                                                                                },
                                                                                "& .MuiAutocomplete-listbox > li": {
                                                                                    width: "100%",
                                                                                    boxSizing: "border-box",
                                                                                },
                                                                                "& .MuiAutocomplete-groupUl": {
                                                                                    width: "100%",
                                                                                    boxSizing: "border-box",
                                                                                    padding: 0,
                                                                                },
                                                                                "& .MuiAutocomplete-groupLabel": {
                                                                                    width: "100%",
                                                                                    boxSizing: "border-box",
                                                                                },
                                                                                "& .MuiAutocomplete-option": {
                                                                                    width: "100%",
                                                                                    boxSizing: "border-box",
                                                                                },
                                                                            },
                                                                        },
                                                                    }}
                                                                    ListboxProps={{
                                                                        style: { width: "100%", boxSizing: "border-box" },
                                                                    }}
                                                                    options={clauseQuestionOptions}
                                                                    getOptionLabel={(option) => option.label}
                                                                    getOptionDisabled={(option) => !!option.disabled}
                                                                    isOptionEqualToValue={(option, value) => option.questionId === value.questionId}
                                                                    value={clauseQuestionOptions.find((opt) => opt.questionId === (clause.questionId ?? question.id)) ?? null}
                                                                    // Widened picker carries a `group` label; render via MUI groupBy.
                                                                    // Legacy unwidened picker has no groups, so skip groupBy entirely.
                                                                    {...(clauseQuestionOptions.some((opt) => opt.group)
                                                                        ? { groupBy: (option: ConditionQuestionOption) => option.group ?? "" }
                                                                        : {})}
                                                                    renderGroup={(params) => (
                                                                        <li
                                                                            key={params.key}
                                                                            style={{ width: "100%", boxSizing: "border-box" }}
                                                                        >
                                                                            <ListSubheader
                                                                                component="div"
                                                                                className="MuiAutocomplete-groupLabel"
                                                                                sx={{
                                                                                    width: "100%",
                                                                                    boxSizing: "border-box",
                                                                                    px: 2,
                                                                                    py: 0.75,
                                                                                    lineHeight: 1.4,
                                                                                }}
                                                                            >
                                                                                {params.group}
                                                                            </ListSubheader>
                                                                            <ul
                                                                                className="MuiAutocomplete-groupUl"
                                                                                style={{ width: "100%", boxSizing: "border-box", padding: 0, margin: 0 }}
                                                                            >
                                                                                {params.children}
                                                                            </ul>
                                                                        </li>
                                                                    )}
                                                                    onChange={(_, newValue) => {
                                                                        if (newValue && onConditionClauseChange) {
                                                                            onConditionClauseChange(clientId, triggerIndex, clauseIndex, "questionId", newValue.questionId);
                                                                        }
                                                                    }}
                                                                    renderOption={(props, option) => {
                                                                        const {
                                                                            key,
                                                                            style,
                                                                            ...optionProps
                                                                        } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                                                                        return (
                                                                            <li
                                                                                key={key}
                                                                                {...optionProps}
                                                                                style={{ ...style, width: "100%", boxSizing: "border-box" }}
                                                                            >
                                                                                {option.label}
                                                                            </li>
                                                                        );
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField {...params} label="Question" size="small" />
                                                                    )}
                                                                />

                                                                <FormControl sx={{ minWidth: 60 }} size="small">
                                                                    <InputLabel id={`operator-${clientId}-${triggerIndex}-${clauseIndex}`}>Operator</InputLabel>
                                                                    <Select
                                                                        labelId={`operator-${clientId}-${triggerIndex}-${clauseIndex}`}
                                                                        label="Operator"
                                                                        value={operatorOptions[0].value}
                                                                        disabled
                                                                    >
                                                                        {operatorOptions.map((option) => (
                                                                            <MenuItem key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>

                                                                {(() => {
                                                                    const clauseWq =
                                                                        (allQuestions ?? []).find((q) => q.question.id === clause.questionId) ??
                                                                        (crossWorkflowSourceQuestions?.codeWorkflows ?? []).find((q) => q.question.id === clause.questionId) ??
                                                                        (crossWorkflowSourceQuestions?.caseWorkflows ?? []).find((q) => q.question.id === clause.questionId);
                                                                    const clauseIsSpan = clauseWq ? isSpanQuestionType(clauseWq.question.questionType) : isSpanQuestionType(question.questionType);
                                                                    const clauseQuestionRequired = clauseWq?.question.required ?? question.required ?? false;

                                                                    if (clauseIsSpan) {
                                                                        return (
                                                                            <FormControl sx={{ flex: 1, minWidth: 0 }} size="small" fullWidth>
                                                                                <InputLabel id={`span-condition-${clientId}-${triggerIndex}-${clauseIndex}`}>Condition</InputLabel>
                                                                                <Select
                                                                                    labelId={`span-condition-${clientId}-${triggerIndex}-${clauseIndex}`}
                                                                                    label="Condition"
                                                                                    value={clause.answerValueIds?.[0] ?? ""}
                                                                                    onChange={(event) => {
                                                                                        const selectedId = Number(event.target.value);
                                                                                        if (onConditionClauseChange) {
                                                                                            onConditionClauseChange(
                                                                                                clientId,
                                                                                                triggerIndex,
                                                                                                clauseIndex,
                                                                                                "answerValueIds",
                                                                                                selectedId ? [selectedId] : [],
                                                                                            );
                                                                                        } else {
                                                                                            const syntheticAnswer = clauseAnswers.find((a) => a.id === selectedId);
                                                                                            onTriggerAnswersChange(
                                                                                                clientId,
                                                                                                triggerIndex,
                                                                                                syntheticAnswer ? [syntheticAnswer] : [],
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {spanConditionOptions
                                                                                        .filter((option) => !(option.value === SPAN_NOT_ANSWERED_ID && clauseQuestionRequired))
                                                                                        .map((option) => (
                                                                                        <MenuItem key={option.value} value={option.value}>
                                                                                            {option.value === SPAN_NOT_ANSWERED_ID ? (
                                                                                                <Tooltip title="If no span is selected, the next question in sequence will be shown automatically" placement="right">
                                                                                                    <span>{option.label}</span>
                                                                                                </Tooltip>
                                                                                            ) : (
                                                                                                option.label
                                                                                            )}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <Autocomplete<AnswerValue, true, false, false>
                                                                            multiple
                                                                            disableCloseOnSelect
                                                                            options={clauseAnswers}
                                                                            getOptionLabel={(option) => option.name ?? option.displayText ?? ""}
                                                                            value={clauseAnswers.filter((answer) => clause.answerValueIds?.includes(answer.id))}
                                                                            onChange={(_, values: AnswerValue[]) => {
                                                                                if (onConditionClauseChange) {
                                                                                    onConditionClauseChange(
                                                                                        clientId,
                                                                                        triggerIndex,
                                                                                        clauseIndex,
                                                                                        "answerValueIds",
                                                                                        values.map((v) => v.id as number),
                                                                                    );
                                                                                } else {
                                                                                    // Fallback for legacy single-condition mode
                                                                                    onTriggerAnswersChange(clientId, triggerIndex, values);
                                                                                }
                                                                            }}
                                                                            renderOption={(props, option, { selected }) => {
                                                                                const { key, ...restProps } = props;
                                                                                const selectedAnswers = clauseAnswers.filter((answer) => clause.answerValueIds?.includes(answer.id));
                                                                                const allSelected = selectedAnswers.length === clauseAnswers.length;
                                                                                const isFirstOption = clauseAnswers.indexOf(option) === 0;

                                                                                return (
                                                                                    <React.Fragment key={key}>
                                                                                        {isFirstOption && clauseAnswers.length > 1 && (
                                                                                            <Box
                                                                                                component="li"
                                                                                                sx={{
                                                                                                    fontWeight: 600,
                                                                                                    borderBottom: "1px solid #e0e0e0",
                                                                                                    py: 1,
                                                                                                    px: 2,
                                                                                                    cursor: "pointer",
                                                                                                    "&:hover": { backgroundColor: "action.hover" },
                                                                                                }}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    if (onConditionClauseChange) {
                                                                                                        onConditionClauseChange(
                                                                                                            clientId,
                                                                                                            triggerIndex,
                                                                                                            clauseIndex,
                                                                                                            "answerValueIds",
                                                                                                            allSelected ? [] : clauseAnswers.map((a) => a.id as number),
                                                                                                        );
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                {allSelected ? "Deselect All" : "Select All"}
                                                                                            </Box>
                                                                                        )}
                                                                                        <Box component="li" {...restProps}>
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={selected}
                                                                                                readOnly
                                                                                                style={{ marginRight: 8 }}
                                                                                            />
                                                                                            {option.name ?? option.displayText ?? ""}
                                                                                        </Box>
                                                                                    </React.Fragment>
                                                                                );
                                                                            }}
                                                                            renderInput={(params) => (
                                                                                <TextField {...params} label="Answers" placeholder="Select answers" size="small" />
                                                                            )}
                                                                            fullWidth
                                                                            sx={{ flex: 1, minWidth: 0 }}
                                                                        />
                                                                    );
                                                                })()}
                                                                {trigger.compoundCondition && trigger.compoundCondition.conditions.length > 1 && (
                                                                    <Tooltip title="Remove condition">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={() => onRemoveConditionClause?.(clientId, triggerIndex, clauseIndex)}
                                                                        >
                                                                            <DeleteOutline fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Stack>
                                                            {showNonCoAssignedWarning && (
                                                                <FormHelperText
                                                                    sx={{ color: "warning.main", ml: 0 }}
                                                                    data-testid={`cross-workflow-source-warning-${clientId}-${triggerIndex}-${clauseIndex}`}
                                                                >
                                                                    This source isn&apos;t currently in any rubric using this workflow — the trigger will silently no-op until the workflows are co-assigned.
                                                                </FormHelperText>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {trigger.compoundCondition && (
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        startIcon={<Add />}
                                                        onClick={() => onAddConditionClause?.(clientId, triggerIndex)}
                                                        sx={{ alignSelf: "flex-start" }}
                                                    >
                                                        Add Condition
                                                    </Button>
                                                )}
                                                {!section.hideActionControls ? (
                                                    <Stack
                                                        direction={{ xs: "column", md: "row" }}
                                                        spacing={1}
                                                        alignItems={{ md: "center" }}
                                                    >
                                                        <Typography variant="subtitle2">Then</Typography>
                                                        <FormControl sx={{ minWidth: 160 }} size="small">
                                                            <InputLabel id={`action-${clientId}-${triggerIndex}`}>Action</InputLabel>
                                                            <Select
                                                                labelId={`action-${clientId}-${triggerIndex}`}
                                                                label="Action"
                                                                value={trigger.action ?? "GO_TO"}
                                                                onChange={(event) =>
                                                                    onTriggerActionChange(
                                                                        clientId,
                                                                        triggerIndex,
                                                                        event.target.value as WorkflowTriggerAction,
                                                                    )
                                                                }
                                                            >
                                                                {(isSpan ? spanActionOptions : actionOptions).map((option) => (
                                                                    <MenuItem key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        {actionConfig[trigger.action || 'GO_TO']?.hasInput && (() => {
                                                        const targetLabel = actionConfig[trigger.action || 'GO_TO']?.inputLabel || 'Action Parameter';
                                                        const targetPlaceholder = actionConfig[trigger.action || 'GO_TO']?.placeholder || 'Add another question to target';
                                                        const triggerTargetOptions = computeTargetOptionsForAction(trigger.action);
                                                        const selectedTarget = triggerTargetOptions.find((opt) => opt.id === trigger.toQuestionId) ?? null;
                                                        return (
                                                            <Autocomplete<{ id: number; name: string }, false, false, false>
                                                                fullWidth
                                                                size="small"
                                                                options={triggerTargetOptions}
                                                                getOptionLabel={(option) => option.name}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                value={selectedTarget}
                                                                noOptionsText={targetPlaceholder}
                                                                onChange={(_, newValue) => {
                                                                    // Pass through `undefined` when the user clears the
                                                                    // selection so the parent can wipe a stale toQuestionId
                                                                    // from trigger state.
                                                                    onTriggerTargetChange(
                                                                        clientId,
                                                                        triggerIndex,
                                                                        newValue?.id,
                                                                    );
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label={targetLabel} size="small" />
                                                                )}
                                                            />
                                                        );
                                                        })()}
                                                        {actionConfig[trigger.action || 'GO_TO']?.needsToAnswerValueIds && (trigger.toQuestionId) && (() => {
                                                        // Find the target question to get its answers, including
                                                        // cross-workflow questions only for actions that support
                                                        // cross-workflow targets.
                                                        const targetSearchPool: WorkflowQuestionWithClientId[] = [
                                                            ...(validTargetQuestions ?? questions),
                                                            ...(supportsCrossWorkflowTargets(trigger.action)
                                                                ? (crossWorkflowTargetQuestions ?? [])
                                                                : []),
                                                        ];
                                                        const targetQuestion = targetSearchPool.find(
                                                            (q) => q.question.id === trigger.toQuestionId
                                                        );
                                                        const targetAnswers = getActiveAnswers(targetQuestion?.question);

                                                        if (targetAnswers.length === 0) {
                                                            return null;
                                                        }

                                                        const isAutofill = trigger.action === "AUTOFILL";

                                                        return (
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel id={`trigger-to-answers-${clientId}-${triggerIndex}`}>
                                                                    {isAutofill ? "Answer to Autofill" : "Answers to Set"}
                                                                </InputLabel>
                                                                {isAutofill ? (
                                                                    <Select
                                                                        labelId={`trigger-to-answers-${clientId}-${triggerIndex}`}
                                                                        placeholder={isAutofill ? "Answer to Autofill" : "Answers to Set"}
                                                                        value={trigger.toAnswerValueIds?.[0] ?? ""}
                                                                        onChange={(event) => {
                                                                            if (onTriggerToAnswersChange) {
                                                                                const answerId = event.target.value ? Number(event.target.value) : undefined;
                                                                                onTriggerToAnswersChange(
                                                                                    clientId,
                                                                                    triggerIndex,
                                                                                    answerId !== undefined ? [answerId] : [],
                                                                                );
                                                                            }
                                                                        }}
                                                                    >
                                                                        {targetAnswers.map((answer) => (
                                                                            <MenuItem key={answer.id} value={answer.id}>
                                                                                {answer.name ?? answer.displayText ?? ""}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                ) : (
                                                                    <Autocomplete<AnswerValue, true, false, false>
                                                                        multiple
                                                                        options={targetAnswers}
                                                                        getOptionLabel={(option) => option.name ?? option.displayText ?? ""}
                                                                        value={targetAnswers.filter((answer) => trigger.toAnswerValueIds?.includes(answer.id))}
                                                                        onChange={(_, values: AnswerValue[]) => {
                                                                            if (onTriggerToAnswersChange) {
                                                                                onTriggerToAnswersChange(
                                                                                    clientId,
                                                                                    triggerIndex,
                                                                                    values.map((v) => v.id as number),
                                                                                );
                                                                            }
                                                                        }}
                                                                        renderInput={(params) => (
                                                                            <TextField {...params} label="Answers to Set" placeholder="Select answers" size="small" />
                                                                        )}
                                                                    />
                                                                )}
                                                            </FormControl>
                                                        );
                                                        })()}
                                                        <Tooltip title="Remove trigger">
                                                            <IconButton
                                                                color="secondary"
                                                                onClick={() => onRemoveTrigger(clientId, triggerIndex)}
                                                                aria-label="Remove trigger"
                                                            >
                                                                <DeleteOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                ) : (
                                                    <Stack direction="row" justifyContent="flex-end">
                                                        <Tooltip title="Remove trigger">
                                                            <IconButton
                                                                color="secondary"
                                                                onClick={() => onRemoveTrigger(clientId, triggerIndex)}
                                                                aria-label="Remove trigger"
                                                            >
                                                                <DeleteOutline />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {triggerDroppableProvided.placeholder}
                        </Stack>
                    )}
                </StrictModeDroppable>
            )}
        </Stack>
    );
};

const TriggerSection = React.memo(TriggerSectionImpl);
TriggerSection.displayName = "TriggerSection";
export default TriggerSection;
